import React from 'react';
import { Modal } from '../ui/Modal';
import { Bell, Shield, Moon, Sun, Monitor } from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { useTheme } from '../../hooks/useTheme';
import { TwoFactorAuth } from './TwoFactorAuth';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function SettingsModal({ isOpen, onClose }: SettingsModalProps) {
  const {
    pushNotificationsEnabled,
    emailNotificationsEnabled,
    twoFactorEnabled,
    togglePushNotifications,
    toggleEmailNotifications,
    toggleTwoFactor,
  } = useUIStore();

  const { theme, setTheme } = useTheme();
  const [showTwoFactorModal, setShowTwoFactorModal] = React.useState(false);

  const handleTwoFactorToggle = () => {
    if (!twoFactorEnabled) {
      setShowTwoFactorModal(true);
    } else {
      if (window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
        toggleTwoFactor();
      }
    }
  };

  const handleTwoFactorComplete = () => {
    toggleTwoFactor();
    setShowTwoFactorModal(false);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title="Settings">
        <div className="space-y-6">
          {/* Notifications Section */}
          <div>
            <h3 className="flex items-center text-lg font-medium text-gray-900">
              <Bell className="mr-2 h-5 w-5 text-gray-400" />
              Notifications
            </h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700">Push Notifications</p>
                  <p className="text-sm text-gray-500">Get instant notifications in your browser</p>
                </div>
                <button
                  role="switch"
                  aria-checked={pushNotificationsEnabled}
                  onClick={togglePushNotifications}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    pushNotificationsEnabled ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      pushNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700">Email Notifications</p>
                  <p className="text-sm text-gray-500">Receive important updates via email</p>
                </div>
                <button
                  role="switch"
                  aria-checked={emailNotificationsEnabled}
                  onClick={toggleEmailNotifications}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    emailNotificationsEnabled ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      emailNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div>
            <h3 className="flex items-center text-lg font-medium text-gray-900">
              <Shield className="mr-2 h-5 w-5 text-gray-400" />
              Security
            </h3>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-700">Two-Factor Authentication</p>
                  <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
                </div>
                <button
                  role="switch"
                  aria-checked={twoFactorEnabled}
                  onClick={handleTwoFactorToggle}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                    twoFactorEnabled ? 'bg-purple-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      twoFactorEnabled ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Appearance Section */}
          <div>
            <h3 className="flex items-center text-lg font-medium text-gray-900">
              <Moon className="mr-2 h-5 w-5 text-gray-400" />
              Appearance
            </h3>
            <div className="mt-4">
              <div className="flex rounded-lg shadow-sm">
                <button
                  onClick={() => setTheme('light')}
                  className={`inline-flex items-center rounded-l-lg border px-3 py-2 text-sm ${
                    theme === 'light'
                      ? 'border-purple-500 bg-purple-50 text-purple-600'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Sun className="mr-2 h-4 w-4" />
                  Light
                </button>
                <button
                  onClick={() => setTheme('dark')}
                  className={`inline-flex items-center border-y px-3 py-2 text-sm ${
                    theme === 'dark'
                      ? 'border-purple-500 bg-purple-50 text-purple-600'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Moon className="mr-2 h-4 w-4" />
                  Dark
                </button>
                <button
                  onClick={() => setTheme('system')}
                  className={`inline-flex items-center rounded-r-lg border px-3 py-2 text-sm ${
                    theme === 'system'
                      ? 'border-purple-500 bg-purple-50 text-purple-600'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Monitor className="mr-2 h-4 w-4" />
                  System
                </button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* 2FA Setup Modal */}
      <TwoFactorAuth
        isOpen={showTwoFactorModal}
        onClose={() => setShowTwoFactorModal(false)}
        onComplete={handleTwoFactorComplete}
      />
    </>
  );
}